import React, { useMemo, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { $getRoot, EditorState } from 'lexical';

import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { TRANSFORMERS } from '@lexical/markdown';
import { calendarEditorConfig } from '../lexical/config/calendarEditorConfig';
import { MentionPlugin } from '../lexical/plugins/MentionPlugin';
import CodeHighlightPlugin from '../lexical/plugins/CodeHighlightPlugin';
import CodePastePlugin from '../lexical/plugins/CodePastePlugin';
import ContextMenuSelectionPlugin from '../lexical/plugins/ContextMenuSelectionPlugin';
import type { ContextMenuSelectionData } from '../lexical/plugins/ContextMenuSelectionPlugin';
import InvoiceToolbarPlugin from '../../billing/lexical/InvoiceToolbarPlugin';

/**
 * Props for Lexical Event Editor
 */
interface LexicalEventEditorProps {
  placeholder?: string;
  onChange?: (content: string) => void;
  onJsonChange?: (json: string) => void;
  className?: string;
  contentEditableClassName?: string;
  placeholderClassName?: string;
  readOnly?: boolean;
  initialJson?: Record<string, any> | null;
  editorKey?: string;
  showToolbar?: boolean;
  fullHeight?: boolean;
  onContextMenu?: (data: ContextMenuSelectionData) => void;
}

/**
 * Lexical-powered event description editor with legal entity references
 * Supports @mentions, #matters, $bills, and /doc references
 */
export function LexicalEventEditor({
  placeholder = 'Enter event description... Try typing @ # $ or /doc to reference entities',
  onChange,
  onJsonChange,
  className = '',
  contentEditableClassName = '',
  placeholderClassName = '',
  readOnly = false,
  initialJson,
  editorKey,
  showToolbar = false,
  fullHeight = false,
  onContextMenu,
}: LexicalEventEditorProps): React.JSX.Element {
  const [editorState, setEditorState] = useState<EditorState | null>(null);

  // Handle editor content changes
  const handleEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    
    newEditorState.read(() => {
      // Get plain text for basic onChange
      const textContent = $getRoot().getTextContent() || '';
      onChange?.(textContent);

      // Get JSON representation for storage
      const jsonString = JSON.stringify(newEditorState.toJSON());
      onJsonChange?.(jsonString);
    });
  };

  const editorConfig = useMemo(() => {
    const hasInitialState = Boolean(initialJson && typeof initialJson === 'object' && (initialJson as any).root);
    return {
      ...calendarEditorConfig,
      editable: !readOnly,
      editorState: hasInitialState ? JSON.stringify(initialJson) : undefined,
    };
  }, [readOnly, initialJson]);

  return (
    <div className={`lexical-event-editor ${fullHeight ? 'min-h-full flex flex-col' : ''} ${className}`}>
      <LexicalComposer key={editorKey || 'default'} initialConfig={editorConfig}>
        <div className={`relative ${fullHeight ? 'flex min-h-full flex-col' : ''}`}>
          {showToolbar && (
            <div className={fullHeight ? 'sticky top-0 z-20 bg-white dark:bg-gray-800' : ''}>
              <InvoiceToolbarPlugin readOnly={readOnly} />
            </div>
          )}
          <div className={`relative ${fullHeight ? 'min-h-full' : ''} ${showToolbar ? 'border border-gray-300 dark:border-gray-600 rounded-b-lg bg-white dark:bg-gray-800 border-t-0' : ''}`}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={`lexical-content-editable outline-none ${fullHeight ? 'min-h-full' : 'min-h-[120px]'} p-3 text-gray-800 dark:text-gray-200 ${contentEditableClassName}`}
                  spellCheck={true}
                  aria-label="Event description editor"
                />
              }
              placeholder={
                <div className={`lexical-placeholder pointer-events-none absolute top-3 left-3 text-gray-500 dark:text-gray-400 select-none ${placeholderClassName}`}>
                  {placeholder}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <HistoryPlugin />
          {showToolbar && <ListPlugin />}
          {showToolbar && <LinkPlugin />}
          <CodeHighlightPlugin />
          <CodePastePlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <HorizontalRulePlugin />
          <OnChangePlugin onChange={handleEditorChange} />
          {!readOnly && <MentionPlugin />}
          {onContextMenu && <ContextMenuSelectionPlugin onContextMenu={onContextMenu} />}
        </div>
      </LexicalComposer>
      
      {/* Debug info - remove in production */}
      {process.env.NODE_ENV === 'development' && editorState && (
        <details className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded text-sm">
          <summary className="cursor-pointer font-medium">Debug: Editor State</summary>
          <pre className="mt-2 text-xs overflow-auto">
            {JSON.stringify(editorState.toJSON(), null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

/**
 * Demo component showing Lexical integration examples
 */
export function LexicalEventEditorDemo(): React.JSX.Element {
  const [content, setContent] = useState('');
  const [jsonContent, setJsonContent] = useState('');

  return (
    <div className="space-y-6 p-6 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          EdgeCalendar Lexical Integration Demo
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          This editor demonstrates deep Lexical integration with legal entity references:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-4">
          <li><strong>@</strong> - Contact mentions (try typing "@john")</li>
          <li><strong>#</strong> - Matter references (try typing "#smith")</li>
          <li><strong>$</strong> - Bill references (try typing "$invoice")</li>
          <li><strong>/doc</strong> - Document references (try typing "/doc motion")</li>
        </ul>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Event Description (with Legal Entity References)
          </label>
          <LexicalEventEditor
            onChange={setContent}
            onJsonChange={setJsonContent}
            className="border border-gray-300 dark:border-gray-600 rounded-lg"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Plain Text Output
            </label>
            <textarea
              className="w-full h-24 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm"
              value={content}
              readOnly
              placeholder="Plain text will appear here..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              JSON State (for Database Storage)
            </label>
            <textarea
              className="w-full h-24 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm font-mono"
              value={jsonContent ? JSON.stringify(JSON.parse(jsonContent), null, 2) : ''}
              readOnly
              placeholder="JSON state will appear here..."
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
          Example Legal Event Descriptions
        </h4>
        <div className="space-y-2 text-sm">
          <div className="p-3 bg-lex-accent-soft rounded border border-lex-accent">
            <strong>Client Meeting:</strong> "Initial consultation with @John-Smith regarding #Smith-v-Jones personal injury case. Review $Invoice-2024-001 and discuss /doc Expert-Medical-Report."
          </div>
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-700">
            <strong>Court Hearing:</strong> "Motion for summary judgment hearing in #ABC-Corp-Merger case. Present /doc Motion-Summary-Judgment to Judge. Coordinate with @Jane-Doe opposing counsel."
          </div>
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-700">
            <strong>Deadline:</strong> "File response to discovery requests in #Estate-Planning-Wilson matter. Review /doc Discovery-Requests and coordinate with @Bob-Johnson paralegal. Bill time to $Invoice-2024-003."
          </div>
        </div>
      </div>
    </div>
  );
} 
